import { CSSProperties, MutableRefObject, useEffect, useRef } from "react"
import { AgTechClickEventHandler, useClickable, AgTechWebClickable } from "../../../Buttons/AgTechButtons"
import { AgTechSelectionPopUp } from "./AgTechSelectionPopUp"
import { AgTechRenderedPopUp, AgTechPopUpPosition } from "@agtech/core/functions/AgTechAppPopUps"
import { AgTechQueryRun } from "@agtech/core/data/queries/AgTechDataQueries"

/// Types

export interface IAgTechSelectionItem {
    id: string | number
}

export type AgTechSelectionItems<TItem extends IAgTechSelectionItem> = {
    [id: string]: {
        item: TItem,
        index: number
    }
}

export type AgTechSelectionControlState<TItem extends IAgTechSelectionItem> = {
    allItems: TItem[],
    visibleSelectionItems: TItem[],
    activeItem?: TItem,
    activeItemListIndex: number,
    freeText?: string,
}

export type AgTechSelectionControlEventHandlers<TItem extends IAgTechSelectionItem> = {
    onItemSelected: (selectedItem: TItem) => Promise<void>,
    onItemsUpdated: (updatedItems: TItem[]) => void
}

/// Props

export type AgTechSelectionProps<TItem extends IAgTechSelectionItem> = {
    items: AgTechSelectionControlItemProps<TItem>,
    content: AgTechSelectionControlContentProps<TItem>
}

export type AgTechSelectionControlProps<TItem extends IAgTechSelectionItem> = AgTechSelectionProps<TItem> & {
    isOpen: boolean
}

export type AgTechSelectionControlItemProps<TItem extends IAgTechSelectionItem> = {
    itemsQuery: AgTechQueryRun<TItem[]>,
    getSelectedItemId: () => string | undefined,
    onItemSelected: (selectedItem: TItem) => Promise<any>,
    searchItems: (item: TItem, filter: string) => boolean,
    filterItems?: (item: TItem) => boolean,
    itemCreationConfig?: AgTechSelectionControlItemCreationProps<TItem>,
    freeText?: string,
}

export type AgTechSelectionControlBehaviorProps = {
    supportsFreetext: boolean,
}

export type AgTechSelectionControlItemCreationProps<TItem> = {
    noRecordsMessage: string,
    noRecordsPrompt: string,
    creationButtonText: string,
    creationButtonAction: (e: AgTechClickEventHandler) => Promise<TItem>
}

export type AgTechSelectionControlContentProps<TItem extends IAgTechSelectionItem> = {
    header: (props: AgTechSelectionControlRenderingProps<TItem>) => React.ReactNode,
    body: (props: AgTechSelectionControlRenderingProps<TItem>) => React.ReactNode,
    footer?: (props: AgTechSelectionControlRenderingProps<TItem>) => React.ReactNode,
    height: number,
    width?: number,
    styles?: CSSProperties
}

export type AgTechSelectionControlRenderingProps<TItem extends IAgTechSelectionItem> = {
    state: AgTechSelectionControlState<TItem>,
    handlers: AgTechSelectionControlEventHandlers<TItem>
}

/// Hooks

export type AgTechSelectionPopUpProps<TItem extends IAgTechSelectionItem> = {
    selectionRef: React.RefObject<HTMLInputElement>,
    selectionProps: AgTechSelectionControlProps<TItem>,
    onPopupOpened?: () => void,
    onPopupClosed?: () => void,
    onPopUpBlurred?: () => void
}

export const useAgTechSelectionPopUp = <TItem extends IAgTechSelectionItem>(props: AgTechSelectionPopUpProps<TItem>) => {
    const selectionPopUpRef = useRef<AgTechRenderedPopUp | null>(null);
    const selectionRefClickable = useClickable(props.selectionRef);

    useEffect(() => {
        if (props.selectionRef.current) {
            props.selectionRef.current.onfocus = () => handleSelectionPopUpFocus(props, selectionPopUpRef, selectionRefClickable);
            props.selectionRef.current.onblur = e => handleSelectionPopUpBlur(e, selectionPopUpRef, props.onPopUpBlurred);
        }
    });
}

const handleSelectionPopUpFocus = <TItem extends IAgTechSelectionItem>(
    props: AgTechSelectionPopUpProps<TItem>,
    selectionPopUpRef: MutableRefObject<AgTechRenderedPopUp | null>,
    selectionRefClickable: AgTechWebClickable
) => {
    if (selectionPopUpRef.current === null) {
        selectionPopUpRef.current = selectionRefClickable.popups.showPopUp({
            callerRef: props.selectionRef,
            popup: () => (
                <AgTechSelectionPopUp
                    selectionRef={props.selectionRef}
                    {...props.selectionProps}
                />
            ),
            config: {
                position: AgTechPopUpPosition.BelowRight,
                fadeInTime: '200ms',
                shouldCloseOnBlur: false
            },
            handlers: {
                onPopUpOpened: () => props.onPopupOpened ? props.onPopupOpened() : null,
                onPopUpClosed: () => {
                    selectionPopUpRef.current = null;

                    if (props.onPopupClosed) {
                        props.onPopupClosed();
                    }
                }
            }
        });
    }
}

const handleSelectionPopUpBlur = (e: any, selectionPopUpRef: MutableRefObject<AgTechRenderedPopUp | null>, onBlurHandler?: () => void) => {
    if (e.relatedTarget && selectionPopUpRef.current) {     
        if (e.relatedTarget.parentElement.id === 'selection-header') {
            return;
        }

        if (selectionPopUpRef.current && selectionPopUpRef.current.popupRef.current &&
            selectionPopUpRef.current.popupRef.current.contains(e.relatedTarget)) {
            return;
        }

        if (onBlurHandler) {
            onBlurHandler();
        }
        
        selectionPopUpRef.current.close();
        selectionPopUpRef.current = null;
    }
}